// ============================================================================
// Consolidated Processors
// Used by dependent-field-processing.js and process-dynamic-content.js
// ============================================================================
import {
  FACE_SIMILARITY_THRESHOLD,
  IDV_AUTOMATED_AUTHENTICATION_SOURCES,
  IDV_SUB_STATUS,
} from 'constants/idv';
import moment from 'moment';

import { getFullCountryName, getFullStateName } from '../countries';

// ============================================================================
// Store Value Processor
// ============================================================================

export const getStoredValue = (values, processorParams = {}) => {
  return processorParams?.storedValue ?? processorParams?.stored_value;
};

const US_RESIDENTIAL_COUNTRIES = new Set([
  'US',
  'USA',
  'United States',
  'United States of America',
]);

export const getIsFcra = (values, processorParams = {}) => {
  const mode = processorParams?.mode;
  const residentialCountry = values?.residential_address?.country;
  const roleCountry = values?.role_location_country;

  if (mode === 'always_true') {
    return true;
  }
  if (mode === 'location_based') {
    if (residentialCountry && US_RESIDENTIAL_COUNTRIES.has(residentialCountry)) {
      return true;
    }
    if (roleCountry && US_RESIDENTIAL_COUNTRIES.has(roleCountry)) {
      return true;
    }
    return false;
  }
  return false;
};

// ============================================================================
// Name Processors
// ============================================================================

// Creates variations of a full name from its components
export const createFullNameVariations = (firstName, middleNames, lastName) => {
  const variations = [];

  // Full name with middle names
  if (firstName && lastName) {
    const fullName = [firstName, middleNames, lastName].filter(Boolean).join(' ');
    variations.push(fullName);

    // First + last name (if different from full name)
    const firstLast = `${firstName} ${lastName}`;
    if (firstLast !== fullName) {
      variations.push(firstLast);
    }
  }

  return variations;
};

// Format names for dropdown options
const formatForDropdown = names =>
  names.map(name => ({
    name,
    value: name,
  }));

// Get current legal names based on form values
const getCurrentNames = formValues => {
  if (!formValues) return [];

  // If single_name_only is true, only use single_name if it exists
  if (formValues.single_name_only) {
    return formValues.single_name ? [formValues.single_name] : [];
  }

  // Otherwise use first/middle/last name fields
  return createFullNameVariations(
    formValues.first_name,
    formValues.middle_names,
    formValues.last_name,
  );
};

// Get all other names (excluding current legal name)
const getOtherNames = formValues => {
  if (!formValues) return [];

  const nameArrays = {
    firstNames: [],
    firstMiddleNames: [],
    lastNames: [],
    fullNames: [],
  };

  const addUnique = (array, value) => {
    if (value && !array.includes(value)) array.push(value);
  };

  // Add current first name for combinations (only if not using single_name_only)
  if (formValues.first_name) {
    addUnique(nameArrays.firstNames, formValues.first_name);

    if (formValues.middle_names) {
      const firstMiddle = `${formValues.first_name} ${formValues.middle_names}`;
      addUnique(nameArrays.firstMiddleNames, firstMiddle);
    }
  }

  // Process other names array
  if (Array.isArray(formValues.other_names)) {
    formValues.other_names.forEach(otherName => {
      if (!otherName?.other_name_category) return;

      const category = otherName.other_name_category;

      switch (category) {
        case 'maiden':
        case 'previous_married_name':
          if (otherName.other_last_name) {
            addUnique(nameArrays.lastNames, otherName.other_last_name);
          }
          break;

        case 'legal_name_change':
        case 'alias':
          if (otherName.other_single_name) {
            addUnique(nameArrays.fullNames, otherName.other_single_name);
          } else {
            if (otherName.other_first_name) {
              addUnique(nameArrays.firstNames, otherName.other_first_name);

              if (otherName.other_middle_names) {
                const firstMiddle = `${otherName.other_first_name} ${otherName.other_middle_names}`;
                addUnique(nameArrays.firstMiddleNames, firstMiddle);
              }

              // Use createFullNameVariations for full name variations
              const variations = createFullNameVariations(
                otherName.other_first_name,
                otherName.other_middle_names,
                otherName.other_last_name,
              );
              variations.forEach(variation => addUnique(nameArrays.fullNames, variation));
            } else if (otherName.other_last_name) {
              addUnique(nameArrays.lastNames, otherName.other_last_name);
            }
          }
          break;
      }
    });
  }

  // Generate combinations with maiden/previous names
  nameArrays.lastNames.forEach(lastName => {
    nameArrays.firstNames.forEach(firstName => {
      addUnique(nameArrays.fullNames, `${firstName} ${lastName}`);
    });

    nameArrays.firstMiddleNames.forEach(firstMiddle => {
      addUnique(nameArrays.fullNames, `${firstMiddle} ${lastName}`);
    });
  });

  return nameArrays.fullNames;
};

// Get all names (current + other)
export const getAllNames = formValues => {
  if (!formValues) return [];
  return [...getCurrentNames(formValues), ...getOtherNames(formValues)];
};

// Get other names formatted for dropdown (used by processDynamicContent)
export const buildOtherNameVersions = formValues => {
  if (!formValues) return [];
  return formatForDropdown(getOtherNames(formValues));
};

// Template helper: Get full name (avoids CSP issues with new Function())
// Prefer single_name if it exists, otherwise construct from first_name + last_name
// Falls back to candidateFullName from processor_params if form fields not available
export const getFullName = (values, processorParams = {}) => {
  let fullName = '';

  // Prefer single_name if it exists
  if (values?.single_name?.trim()) {
    fullName = values.single_name.trim();
  } else {
    // Construct from first_name + last_name
    const firstName = values?.first_name?.trim() || '';
    const lastName = values?.last_name?.trim() || '';
    if (firstName || lastName) {
      fullName = `${firstName} ${lastName}`.trim();
    }
  }

  // Fallback to candidate full name from processor params (camelCased by frontend)
  return fullName || processorParams.candidateFullName || '';
};

// ============================================================================
// Location Processors
// ============================================================================

// Constructs role location from state and country (using full names)
export const getRoleLocationName = values => {
  if (!values) return '';
  // Construct location from available location fields
  const parts = [];

  // Get full state name if state code is provided
  if (values.role_location_state?.trim()) {
    const fullStateName = getFullStateName(
      values.role_location_state.trim(),
      values.role_location_country,
    );
    if (fullStateName) parts.push(fullStateName);
  }

  // Get full country name if country code is provided
  if (values.role_location_country?.trim()) {
    const fullCountryName = getFullCountryName(values.role_location_country.trim());
    if (fullCountryName) parts.push(fullCountryName);
  }

  return parts.join(', ') || '';
};

// ============================================================================
// Country Processors
// ============================================================================

const DATE_FORMAT = 'DD/MM/YYYY';
const parseDate = date => moment(date, DATE_FORMAT, true);

// Address values are stored inside an address object (google_address, street_address, country, etc.)
const getCountryFromAddressObject = addressObj => {
  if (!addressObj || typeof addressObj !== 'object') return null;
  const c = addressObj.country;
  return c !== undefined && c !== null && c !== '' ? c : null;
};

export const getUniqueCountries = formValues => {
  const countries = new Set();

  // Residential address (subfields are not nested under object name)
  const residentialAddressObj = formValues?.residential_address;
  const residential = getCountryFromAddressObject(residentialAddressObj);
  if (residential) countries.add(residential);

  const previous = Array.isArray(formValues?.previous_addresses)
    ? formValues.previous_addresses
    : [];

  previous.forEach(entry => {
    const addressObj = entry?.previous_address;
    const country = getCountryFromAddressObject(addressObj);
    if (country) countries.add(country);
  });

  return Array.from(countries);
};

export const getCountriesToCheck = (formValues, config = {}) => {
  if (!formValues || config?.mode !== 'address_history') return [];

  const countryDurations = new Map();
  const now = moment();
  const checkPeriodYears = config.subcheckRequiredAddressYears || 10;
  const cutoffDate = moment().subtract(checkPeriodYears, 'years');

  // Convert addresses into sorted date ranges with country info
  let dateRanges = [];

  // Add current address (residential_address at top level)
  const residentialAddressObj = formValues?.residential_address;
  const residentialCountry = getCountryFromAddressObject(residentialAddressObj);
  if (residentialCountry && formValues.current_address_residence_from) {
    const fromDate = parseDate(formValues.current_address_residence_from);
    if (fromDate.isValid()) {
      dateRanges.push({
        country: residentialCountry,
        from: fromDate,
        to: now,
      });
    }
  }

  // Add previous addresses (each entry has previous_address object with country)
  if (Array.isArray(formValues.previous_addresses)) {
    dateRanges = dateRanges.concat(
      formValues.previous_addresses
        .filter(address => {
          const country = getCountryFromAddressObject(address?.previous_address);
          return (
            country &&
            address?.previous_address_residence_from &&
            address?.previous_address_residence_to
          );
        })
        .map(address => ({
          country: getCountryFromAddressObject(address.previous_address),
          from: parseDate(address.previous_address_residence_from),
          to: parseDate(address.previous_address_residence_to),
        }))
        .filter(range => range.from.isValid() && range.to.isValid()),
    );
  }

  // Sort ranges by 'to' date, descending
  dateRanges.sort((a, b) => b.to.valueOf() - a.to.valueOf());

  // Process each range
  dateRanges.forEach(range => {
    // Check if any part of the range falls within check period
    if (range.from.isBefore(now) && range.to.isAfter(cutoffDate)) {
      const durationMonths = range.to.diff(range.from, 'months');

      if (durationMonths > 0) {
        countryDurations.set(
          range.country,
          (countryDurations.get(range.country) || 0) + durationMonths,
        );
      }
    }
  });

  // Filter countries by minimum residence duration
  const minMonths = config.minimumResidenceMonths || 6;
  const qualifyingCountries = Array.from(countryDurations.entries())
    .filter(([_, duration]) => duration >= minMonths)
    .map(([country]) => country);

  // Add mustIncludeCountries countries
  const mustIncludeCountries = Array.isArray(config.mustIncludeCountries)
    ? config.mustIncludeCountries.filter(Boolean)
    : [];

  // Combine and deduplicate countries
  const finalCountries = [...new Set([...mustIncludeCountries, ...qualifyingCountries])];

  // Apply cap if specified
  if (config.countriesCap) {
    if (mustIncludeCountries.length >= config.countriesCap) {
      return mustIncludeCountries;
    }

    const cappedFinal = finalCountries.slice(0, config.countriesCap);
    return cappedFinal;
  }
  return finalCountries;
};

// ============================================================================
// ID Verification Processors
// ============================================================================

export const getNationalityAlpha2 = values => {
  const nationalitySelection = values?.nationality_selection;
  const auNzCitizen = values?.au_nz_citizen;
  const passportNationality = values?.passport_nationality;

  if (nationalitySelection) return nationalitySelection;
  if (auNzCitizen === 'au_citizen') return 'AU';
  if (auNzCitizen === 'nz_citizen') return 'NZ';

  return passportNationality || undefined;
};

export const getNationalityFullName = values => {
  const alpha2 = getNationalityAlpha2(values);
  return alpha2 ? getFullCountryName(alpha2) : undefined;
};

/** True if India is in countries_to_check (address-based subcheck or client-selected) or in any academic qualification institution country. */
export const hasIndiaInAddressOrQualifications = values => {
  const INDIA_CODES = ['IN', 'India'];
  const isIndia = v => v && INDIA_CODES.includes(String(v).trim());

  const countriesToCheck = values?.criminal_history_subcheck_countries_to_check;
  if (Array.isArray(countriesToCheck) && countriesToCheck.some(isIndia)) return true;

  const clientCountriesToCheck = values?.client_countries_to_check;
  if (Array.isArray(clientCountriesToCheck) && clientCountriesToCheck.some(isIndia)) return true;

  const quals = values?.academic_qualifications;
  if (Array.isArray(quals) && quals.some(item => isIndia(item?.qualification_institution_country)))
    return true;

  return false;
};

/** Canonical father's name: from fathers_name_birth (typed in birth section when country=IN) or fathers_name_additional (typed in additional identity). Object-group subfields use field_code as top-level key. Only this merged value is stored in DB. */
export const getFathersNameResolved = values => {
  const fromBirthInput = values?.fathers_name_birth;
  if (fromBirthInput && String(fromBirthInput).trim() !== '') return fromBirthInput.trim();
  const fromAdditional = values?.fathers_name_additional;
  if (fromAdditional && String(fromAdditional).trim() !== '') return fromAdditional.trim();
  return undefined;
};

// Old code workaround for Woolworths
export const getPassportOrIdNumber = values => {
  const passportNumber = values?.passport_number;
  return passportNumber || undefined;
};

export function getIsPhotoIdSelected(values, processorParams = {}) {
  // First check if we have actual verification data from ID uploads
  if (values?.hasPhotoId === true) {
    return true;
  }

  // Fallback to checking ID type if no verification data available
  const selectedIdTypes = values?.selectedIdTypes || {};
  const photoIdOptions = processorParams.photoIdOptions || [];

  if (photoIdOptions.length === 0) return false;

  return Object.keys(selectedIdTypes).some(t => photoIdOptions.includes(t));
}

export function getIsDobIdSelected(values, processorParams = {}) {
  // First check if we have actual verification data from ID uploads
  // includes_dob comes from Regula verification indicating the ID contains a DOB
  if (values?.hasDobId === true) {
    return true;
  }

  // Fallback to checking ID type if no verification data available
  const selectedIdTypes = values?.selectedIdTypes || {};
  const dobIdOptions = processorParams.dobIdOptions || [];

  if (dobIdOptions.length === 0) return false;

  return Object.keys(selectedIdTypes).some(t => dobIdOptions.includes(t));
}

export function getHasFaceSimilarityPassed(values) {
  const uploads = values?.validUploads || [];

  for (const u of uploads) {
    const faceSimilarity = u?.id_upload_detail?.face_similarity;

    // If face_similarity is null, this ID doesn't apply face similarity
    if (faceSimilarity === null) continue;

    // If we have a score and it meets the threshold, return true
    if (typeof faceSimilarity === 'number' && faceSimilarity >= FACE_SIMILARITY_THRESHOLD) {
      return true;
    }
  }

  return false;
}

export function getHasIdVerifiedByProvider(values) {
  const uploads = values?.validUploads || [];

  for (const u of uploads) {
    const authenticationSource = u?.id_upload_detail?.authentication_source;
    const hasPhoto = u?.id_upload_detail?.has_photo;

    if (!IDV_AUTOMATED_AUTHENTICATION_SOURCES.includes(authenticationSource) || !hasPhoto) continue;

    return true;
  }

  return false;
}

export function getIdValuesSelected(values) {
  const uploads = values?.validUploads || [];
  return uploads.map(u => u?.metadata?.id_type || null).filter(Boolean);
}

export function getIdTypesSelected(values) {
  const selectedIdTypes = values?.selectedIdTypes || {};
  return Object.keys(selectedIdTypes);
}

export function getIdRequirementsListsUsed(values) {
  const uploads = values?.validUploads || [];
  return uploads.map(u => u?.id_upload_detail?.id_requirements_list).filter(Boolean);
}

const US_CRIMINAL_AND_FACIS_SLUGS = new Set([
  'federal_criminal',
  'federal_county_criminal',
  'county_criminal',
  'facis_3',
]);

/** Drives visibility of other-name / qualification linkage docs (hidden field + wrapper conditions). */
export const getOtherNameLinkageSectionRequired = (values, processorParams = {}) => {
  const slugs = processorParams.checkTypeSlugs || processorParams.check_type_slugs || [];
  if (Array.isArray(slugs) && slugs.some(s => US_CRIMINAL_AND_FACIS_SLUGS.has(s))) {
    return false;
  }

  const uploads = values?.validUploads || [];
  // Only unresolved name-on-ID checks should force this section open. "passed" means the ID name
  // already matched the candidate — not a case where linkage docs are needed for that reason.
  const hasUnresolvedNameOnId = uploads.some(u => {
    const s = u?.id_upload_detail?.name_match_status;
    if (
      s == null ||
      s === '' ||
      s === IDV_SUB_STATUS.NOT_APPLICABLE ||
      s === IDV_SUB_STATUS.PASSED
    ) {
      return false;
    }
    return s === IDV_SUB_STATUS.PENDING || s === IDV_SUB_STATUS.FAILED;
  });
  if (hasUnresolvedNameOnId) {
    return true;
  }

  const otherNamesYesNo = values?.other_names_yes_no;
  if (typeof otherNamesYesNo === 'string' && otherNamesYesNo.toLowerCase() === 'yes') {
    return true;
  }

  return false;
};

// ============================================================================
// Address Occupancy Processors
// ============================================================================

// Calculate occupancy between two dates (or from start to now if no end date)
// Returns string like "2 years 8 months (32 months)" or null if invalid
const calculateOccupancy = (fromDateStr, toDateStr = null) => {
  if (!fromDateStr) return null;

  const fromDate = parseDate(fromDateStr);
  if (!fromDate.isValid()) return null;

  const toDate = toDateStr ? parseDate(toDateStr) : moment();
  if (!toDate.isValid()) return null;

  const totalMonths = toDate.diff(fromDate, 'months');
  if (totalMonths < 0) return null;

  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;

  let duration;
  if (years === 0) {
    duration = `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  } else if (remainingMonths === 0) {
    duration = `${years} year${years !== 1 ? 's' : ''}`;
  } else {
    duration = `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  }

  return `${duration} (${totalMonths} months)`;
};

// Get address key - prefer google_address, fall back to street_address
const getAddressKey = addressObj => {
  if (!addressObj) return null;
  return addressObj.google_address || addressObj.street_address || null;
};

// Calculate occupancy for the current residential address (from start date to now)
// Returns string like "2 years 8 months (32 months)"
export const getCurrentAddressOccupancy = formValues => {
  return calculateOccupancy(formValues?.current_address_residence_from, null);
};

// Calculate occupancy for all previous addresses
// Returns object keyed by address: { "123 Main St, City": "1 year (12 months)", ... }
export const getPreviousAddressesOccupancy = formValues => {
  const previousAddresses = formValues?.previous_addresses;
  if (!Array.isArray(previousAddresses) || previousAddresses.length === 0) {
    return {};
  }

  const result = {};
  previousAddresses.forEach(entry => {
    const addressKey = getAddressKey(entry?.previous_address);
    if (!addressKey) return;

    const occupancy = calculateOccupancy(
      entry?.previous_address_residence_from,
      entry?.previous_address_residence_to,
    );

    if (occupancy) {
      result[addressKey] = occupancy;
    }
  });

  return result;
};
